import { Fragment, useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import type { NavItem } from '@/products/registry';
import { PolygonIcon } from './icons/PolygonIcon';
import clsx from 'clsx';
import { useAuthStore } from '@/store/auth';
import { useLogout, useProductAccess } from '@/hooks/useAuth';
import { apiClient } from '@/api/client';
import { getProductBySlug } from '@/types/products';
import { TerraPanel, TerraToggle } from '@/components/terra';
import { CARIPanel } from '@/components/cari/CARIPanel';
import { CARIButton } from '@/components/cari/CARIButton';
import { FeedbackButton } from '@/components/feedback';
import { useTerraStore } from '@/store/terra';
import { getProductConfig, resolveProductSlug } from '@/products/registry';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';

// Lazy load to avoid circular dependency with data-vault components
const VizCategoryPanel = lazy(() =>
  import('@/pages/data-vault/components/VizCategoryPanel').then(m => ({ default: m.VizCategoryPanel }))
);

// ── Nav item rendering ────────────────────────────────────────────────────
// Supports both flat links and expandable groups (item.children). Backward
// compatible — items without `children` render exactly as before.

function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.href && (pathname === item.href || pathname.startsWith(item.href + '/'))) return true;
  if (item.children) return item.children.some((c) => isItemActive(c, pathname));
  return false;
}

interface NavItemRowProps {
  item: NavItem;
  pathname: string;
  accentBorderL: string;          // e.g. 'border-l-cyan-500/30'
  iconSize: string;               // 'h-4 w-4' (desktop) | 'h-5 w-5' (mobile)
  collapsed?: boolean;            // desktop collapsed mode (icon-only)
  onLeafClick?: () => void;       // mobile close-on-click
  depth?: number;                 // 0 = root, 1 = inside an expanded group
}

function NavItemRow({
  item, pathname, accentBorderL, iconSize, collapsed = false, onLeafClick, depth = 0,
}: NavItemRowProps) {
  const active = isItemActive(item, pathname);
  // Open the group by default if any child is active.
  const [expanded, setExpanded] = useState<boolean>(active);

  // Indent children one level.
  const indentClass = depth > 0 ? 'pl-7' : '';

  // ── Expandable group ─────────────────────────────────────────────────
  if (item.children && item.children.length > 0) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          title={collapsed ? item.name : undefined}
          aria-expanded={expanded}
          className={clsx(
            active
              ? 'text-mono-200'
              : 'text-mono-500 hover:text-mono-300',
            'group w-full flex items-center gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors border-l-2 border-transparent hover:bg-mono-800',
            collapsed && 'justify-center'
          )}
        >
          <item.icon className={`${iconSize} shrink-0`} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              {expanded
                ? <ChevronDownIcon className="h-3 w-3 shrink-0" />
                : <ChevronRightIcon className="h-3 w-3 shrink-0" />}
            </>
          )}
        </button>
        {/* Children render only when expanded AND sidebar isn't icon-only */}
        {expanded && !collapsed && (
          <ul className="space-y-1 mt-1">
            {item.children.map((child) => (
              <NavItemRow
                key={child.name}
                item={child}
                pathname={pathname}
                accentBorderL={accentBorderL}
                iconSize={iconSize}
                onLeafClick={onLeafClick}
                depth={depth + 1}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // ── Leaf link ────────────────────────────────────────────────────────
  return (
    <li>
      <Link
        to={item.href}
        onClick={onLeafClick}
        title={collapsed ? item.name : undefined}
        className={clsx(
          active
            ? `bg-mono-800 text-mono-100 border-l-2 ${accentBorderL}`
            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
          indentClass,
          collapsed && 'justify-center'
        )}
      >
        <item.icon className={`${iconSize} shrink-0`} />
        {!collapsed && item.name}
      </Link>
    </li>
  );
}

export function ProductLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const location = useLocation();
  const { user, getEffectiveRole } = useAuthStore();
  const logoutMutation = useLogout();
  const terraOpen = useTerraStore((s) => s.isOpen);
  // Org owner / admins see the Organization Admin link; invited seats don't.
  const effectiveRole = getEffectiveRole();
  const isAdmin = effectiveRole === 'admin' || effectiveRole === 'superadmin';
  // Full interactive CARI is a dashboard-tier feature; command tier gets only
  // template report generation (the Reporting panel on each site).
  const [cariFull, setCariFull] = useState(false);

  // Use shared React Query hook — deduplicates across all components (staleTime: 30s)
  const { data: accessData } = useProductAccess();
  // Slugs we never want to surface in the sidebar even if a backend admin
  // record still lists them. Keep in sync with ClientDashboard's deny-list.
  const PRODUCT_DENYLIST = new Set(['polygon2']);
  const accessibleProducts = (
    accessData?.all_access
      ? ['precept', 'polygon', 'pounce', 'precipitate', 'phreatic', 'pathfinder', 'datavault']
      : (accessData?.products || [])
  ).filter((slug: string) => !PRODUCT_DENYLIST.has(slug));

  // Determine which product we're in based on URL
  const pathParts = location.pathname.split('/');
  const rawSlug = pathParts[1] || 'polygon';
  const productSlug = resolveProductSlug(rawSlug);
  const product = getProductBySlug(productSlug);
  const config = getProductConfig(productSlug);
  // Strip nav items the current user isn't entitled to (e.g. superadmin-only tabs).
  const navigation = config.navigation.filter(
    (item) => !item.requiresSuperadmin || user?.role === 'superadmin'
  );
  const accent = config.accentColor;
  const ProductIcon = config.icon || PolygonIcon;

  // Other accessible products (not current)
  const otherProducts = accessibleProducts.filter(p => p !== productSlug);

  // Chart selection state (URL search params, only relevant for Data Vault)
  const [searchParams, setSearchParams] = useSearchParams();
  const isDataVault = productSlug === 'datavault';
  // CARI is the Phreatic-specialist agent. Mount her surfaces only when the
  // user is inside Phreatic — other products keep TERRA as their assistant.
  const isPhreatic = productSlug === 'phreatic';

  useEffect(() => {
    if (!isPhreatic) { setCariFull(false); return; }
    apiClient.get('/phreatic/billing/license')
      .then((r) => setCariFull(r.data?.effective_tier === 'dashboard'))
      .catch(() => setCariFull(false));
  }, [isPhreatic]);

  const activeChart = searchParams.get('chart');
  const handleSelectChart = (chartId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('chart', chartId);
      return next;
    }, { replace: true });
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <div className={`min-h-screen transition-[margin] duration-300 ${terraOpen ? 'mr-[420px]' : ''}`} style={{ background: 'var(--dv-bg-primary, #0a0a0a)', color: 'var(--dv-text-primary, #f5f5f5)', borderColor: 'var(--dv-border-subtle, rgba(64,64,64,0.3))' }}>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0" style={{ background: 'var(--dv-bg-overlay, rgba(0,0,0,0.6))' }} />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r px-6 pb-4" style={{ background: 'var(--dv-bg-secondary, #171717)', borderColor: 'var(--dv-border-subtle, rgba(64,64,64,0.3))' }}>
                  {/* Product header */}
                  <div className="flex h-16 shrink-0 items-center border-b">
                    <Link to="/" className="flex items-center gap-2">
                      <ProductIcon className={`h-6 w-6 ${accent.text}`} />
                      <span className={`text-lg font-bold text-mono-100 tracking-widest font-orbitron`}>
                        {product?.name || 'POLYGON'}
                      </span>
                    </Link>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-2">NAVIGATION</p>
                        <ul className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <NavItemRow
                              key={item.name}
                              item={item}
                              pathname={location.pathname}
                              accentBorderL={accent.border.replace('border-', 'border-l-')}
                              iconSize="h-5 w-5"
                              onLeafClick={() => setSidebarOpen(false)}
                            />
                          ))}
                        </ul>
                      </li>
                      {/* Organization Admin — org owner / admins only */}
                      {isAdmin && (
                        <li>
                          <Link
                            to="/account"
                            onClick={() => setSidebarOpen(false)}
                            className="group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors text-mono-400 hover:bg-mono-800 hover:text-mono-200 border-l-2 border-transparent"
                          >
                            <BuildingOffice2Icon className="h-5 w-5 shrink-0" />
                            ORGANIZATION
                          </Link>
                        </li>
                      )}
                      {/* Visualization category panel (Data Vault only) - mobile */}
                      {isDataVault && location.pathname === '/data-vault/charts' && (
                        <li>
                          <Suspense fallback={<div className="px-2 py-4 text-mono-600 text-xs">Loading...</div>}>
                            <VizCategoryPanel
                              activeChart={activeChart}
                              onSelectChart={(id) => { handleSelectChart(id); setSidebarOpen(false); }}
                              collapsed={false}
                            />
                          </Suspense>
                        </li>
                      )}
                      {/* Other accessible products - mobile */}
                      {otherProducts.length > 0 && (
                        <li>
                          <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-2">YOUR PRODUCTS</p>
                          <ul className="-mx-2 space-y-1">
                            {otherProducts.map((slug) => {
                              const otherConfig = getProductConfig(slug);
                              const OtherIcon = otherConfig.icon || PolygonIcon;
                              const otherAccent = otherConfig.accentColor;
                              const otherLanding = otherConfig.landingPage;
                              return (
                                <li key={slug}>
                                  <Link
                                    to={otherLanding}
                                    onClick={() => setSidebarOpen(false)}
                                    className="group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors text-mono-400 hover:bg-mono-800 hover:text-mono-200 border-l-2 border-transparent"
                                  >
                                    <OtherIcon className={`h-5 w-5 shrink-0 ${otherAccent.text}`} />
                                    {getProductBySlug(slug)?.name || slug.toUpperCase()}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className={clsx(
        'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
        collapsed ? 'lg:w-14' : 'lg:w-56'
      )}>
        <div className={clsx(
          'flex grow flex-col gap-y-5 overflow-y-auto border-r pb-4 transition-all duration-300',
          collapsed ? 'px-2' : 'px-4'
        )} style={{ background: 'var(--dv-bg-secondary, #171717)', borderColor: 'var(--dv-border-subtle, rgba(64,64,64,0.3))' }}>
          {/* Product header with collapse arrow */}
          <div className="flex h-16 shrink-0 items-center border-b">
            <div className="flex items-center gap-2 flex-1 overflow-hidden">
              <ProductIcon className={`h-5 w-5 shrink-0 ${accent.text}`} />
              {!collapsed && (
                <span className="text-lg font-bold text-mono-100 tracking-[0.2em] font-orbitron whitespace-nowrap">
                  {product?.name || 'POLYGON'}
                </span>
              )}
            </div>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 text-mono-500 hover:text-mono-300 transition-colors shrink-0"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeftIcon className={clsx('h-4 w-4 transition-transform duration-300', collapsed && 'rotate-180')} />
            </button>
          </div>

          {/* Back to marketplace */}
          <Link
            to="/dashboard"
            title={collapsed ? 'ALL PRODUCTS' : undefined}
            className={clsx(
              'flex items-center gap-2 px-2 py-2 text-xs text-mono-500 hover:text-mono-300 tracking-wider transition-colors',
              collapsed && 'justify-center'
            )}
          >
            <Squares2X2Icon className="h-4 w-4 shrink-0" />
            {!collapsed && 'ALL PRODUCTS'}
          </Link>

          {/* Organization Admin — org owner / admins only (seats don't see it). */}
          {isAdmin && (
            <Link
              to="/account"
              title={collapsed ? 'ORGANIZATION' : undefined}
              className={clsx(
                'flex items-center gap-2 px-2 py-2 text-xs tracking-wider transition-colors',
                location.pathname === '/account'
                  ? 'text-cyan-300'
                  : 'text-mono-500 hover:text-mono-300',
                collapsed && 'justify-center'
              )}
            >
              <BuildingOffice2Icon className="h-4 w-4 shrink-0" />
              {!collapsed && 'ORGANIZATION'}
            </Link>
          )}

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                {!collapsed && <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-2">NAVIGATION</p>}
                <ul className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <NavItemRow
                      key={item.name}
                      item={item}
                      pathname={location.pathname}
                      accentBorderL={accent.border.replace('border-', 'border-l-')}
                      iconSize="h-4 w-4"
                      collapsed={collapsed}
                    />
                  ))}
                </ul>
              </li>
              {/* Visualization category panel (Data Vault only) */}
              {isDataVault && location.pathname === '/data-vault/charts' && (
                <li>
                  <Suspense fallback={<div className="px-2 py-4 text-mono-600 text-xs">Loading...</div>}>
                    <VizCategoryPanel
                      activeChart={activeChart}
                      onSelectChart={handleSelectChart}
                      collapsed={collapsed}
                    />
                  </Suspense>
                </li>
              )}
              {/* Other accessible products */}
              {otherProducts.length > 0 && (
                <li>
                  {!collapsed && <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-2">YOUR PRODUCTS</p>}
                  <ul className="-mx-2 space-y-1">
                    {otherProducts.map((slug) => {
                      const otherConfig = getProductConfig(slug);
                      const OtherIcon = otherConfig.icon || PolygonIcon;
                      const otherAccent = otherConfig.accentColor;
                      const otherLanding = otherConfig.landingPage;
                      const isExternal = otherLanding.startsWith('http');
                      return (
                        <li key={slug}>
                          {isExternal ? (
                            <a
                              href={otherLanding}
                              title={collapsed ? (getProductBySlug(slug)?.name || slug.toUpperCase()) : undefined}
                              className={clsx(
                                'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                                collapsed && 'justify-center'
                              )}
                            >
                              <OtherIcon className={`h-4 w-4 shrink-0 ${otherAccent.text}`} />
                              {!collapsed && (getProductBySlug(slug)?.name || slug.toUpperCase())}
                            </a>
                          ) : (
                            <Link
                              to={otherLanding}
                              title={collapsed ? (getProductBySlug(slug)?.name || slug.toUpperCase()) : undefined}
                              className={clsx(
                                'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                                collapsed && 'justify-center'
                              )}
                            >
                              <OtherIcon className={`h-4 w-4 shrink-0 ${otherAccent.text}`} />
                              {!collapsed && (getProductBySlug(slug)?.name || slug.toUpperCase())}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
              <li className="mt-auto">
                <div className={clsx(
                  'flex items-center gap-x-3 px-2 py-3 text-xs tracking-wider border-t',
                  collapsed && 'justify-center'
                )}>
                  <div className={`h-7 w-7 shrink-0 rounded border ${accent.border} ${accent.bg} flex items-center justify-center ${accent.text}`}>
                    {user?.full_name?.[0] || user?.email?.[0] || 'U'}
                  </div>
                  {!collapsed && (
                    <>
                      <div className="flex-1 truncate">
                        <p className="text-mono-200 truncate">{user?.email}</p>
                        <p className="text-mono-500 uppercase text-[10px]">{user?.role}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="p-1 hover:bg-mono-800 rounded text-mono-500 hover:text-mono-300"
                        title="Logout"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={clsx('transition-all duration-300', collapsed ? 'lg:pl-14' : 'lg:pl-56')}>
        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b px-4 lg:hidden" style={{ background: 'var(--dv-bg-secondary, #171717)', borderColor: 'var(--dv-border-subtle, rgba(64,64,64,0.3))' }}>
          <button
            type="button"
            className="-m-2.5 p-2.5 text-mono-400 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-2">
              <ProductIcon className={`h-5 w-5 ${accent.text}`} />
              <span className="text-lg font-bold text-mono-100 tracking-[0.2em] font-orbitron">
                {product?.name || 'POLYGON'}
              </span>
            </div>
          </div>
        </div>

        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* On Phreatic, full interactive CARI is a DASHBOARD-tier feature — the
          command tier gets template reports only (no live agent). Everywhere
          else, TERRA is the assistant. One button per surface — no doubles. */}
      {isPhreatic ? (
        cariFull ? (
          <>
            <CARIPanel />
            <CARIButton />
          </>
        ) : null
      ) : (
        <>
          <TerraPanel />
          <TerraToggle />
        </>
      )}

      <FeedbackButton />
    </div>
  );
}
